import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Boxes } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import './CrossDiscovery.css';

/**
 * CrossDiscovery — shared section rendered on marketing pages to
 * cross-link between host integrations.
 *
 * `currentPage` prop excludes the current page from the grid so we don't
 * render e.g. "OpenClaw" as a card on /openclaw itself.
 *
 * Destinations:
 *   - linear      → /linear (dedicated marketing page)
 *   - openclaw    → /openclaw (dedicated marketing page)
 *   - cursor      → /delegate/cursor (management page, no
 *                   dedicated marketing yet — future PR)
 *   - claude-code → /delegate/claude-code (same)
 *   - other       → /delegate/other (catch-all)
 */
const HOSTS = [
  {
    key: 'linear',
    name: 'Linear',
    blurb: 'Delegate Linear issues to CellCog. Research, documents, visuals — delivered as native agent activities.',
    to: '/linear',
    logoLight: '/assets/icons/linear-dark.svg',
    logoDark: '/assets/icons/linear.svg',
  },
  {
    key: 'openclaw',
    name: 'OpenClaw',
    blurb: 'The any-to-any execution layer for OpenClaw agents. Install once, delegate everything.',
    to: '/openclaw',
    logoLight: '/assets/icons/agents/openclaw.svg',
    logoDark: '/assets/icons/agents/openclaw.svg',
  },
  {
    key: 'cursor',
    name: 'Cursor',
    blurb: 'Plug CellCog into your coding agent via Open Plugins. /cellcog in the command menu.',
    to: '/delegate/cursor',
    logoLight: '/assets/icons/agents/cursor-light.svg',
    logoDark: '/assets/icons/agents/cursor-dark.svg',
  },
  {
    key: 'claude-code',
    name: 'Claude Code',
    blurb: "Anthropic's coding agent meets CellCog. Slash commands light up multimodal capabilities.",
    to: '/delegate/claude-code',
    logoLight: '/assets/icons/agents/claude.png',
    logoDark: '/assets/icons/agents/claude.png',
  },
  {
    key: 'other',
    name: 'Your own agents',
    blurb: 'Custom scripts, home-grown frameworks, direct HTTP callers — CellCog works wherever HTTP does.',
    to: '/delegate/other',
    icon: <Boxes size={28} />,
  },
];

const CrossDiscovery = ({ currentPage }) => {
  const { isDarkMode } = useTheme();
  const items = HOSTS.filter((h) => h.key !== currentPage);

  return (
    <section className="openclaw-cross">
      <div className="section section-narrow">
        <div className="section-eyebrow">Wherever you work</div>
        <h2 className="section-title display-font">
          CellCog lives in the tools your team already uses.
        </h2>
        <p className="section-subtitle">
          One CellCog account, many surfaces. Add a Linear workspace, a Cursor
          plugin, a Claude Code install, or an API key to your custom agent.
        </p>

        <div className="openclaw-cross__grid">
          {items.map((h) => {
            const logoSrc = isDarkMode ? h.logoDark : h.logoLight;
            return (
              <Link
                key={h.key}
                to={h.to}
                className="openclaw-cross__card"
                aria-label={`${h.name} — ${h.blurb}`}
              >
                <div className="openclaw-cross__card-icon">
                  {h.icon
                    ? h.icon
                    : (
                      /* TWO-FACE RULE (adaptive shell): shell pins dark, so
                         logoSrc is the dark-face artwork there; where the
                         light artwork differs, a twin renders for the
                         adaptive light face only. Logged-in unchanged. */
                      <>
                        <img
                          src={logoSrc}
                          alt=""
                          className={`openclaw-cross__card-img${h.logoLight !== h.logoDark ? ' mkt-face-dark' : ''}`}
                        />
                        {h.logoLight !== h.logoDark && (
                          <img
                            src={h.logoLight}
                            alt=""
                            className="openclaw-cross__card-img mkt-face-light"
                          />
                        )}
                      </>
                    )}
                </div>
                <div className="openclaw-cross__card-body">
                  <div className="openclaw-cross__card-title">
                    {h.name}
                    <ArrowUpRight size={14} className="openclaw-cross__card-arrow" />
                  </div>
                  <div className="openclaw-cross__card-blurb">{h.blurb}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CrossDiscovery;
